import { useState, useEffect, useRef } from 'react';
import {
    Play,
    Pause,
    Download,
    ChevronDown,
    ChevronUp,
    Clock,
    CheckCircle2,
    Loader2,
    AlertCircle,
    Bell
} from 'lucide-react';
import { ope, user as userApi, API_BASE } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import NativeService from '../../services/nativeService';

const AudioTab = () => {
    const [temas, setTemas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedTema, setExpandedTema] = useState(null);
    const [secciones, setSecciones] = useState({});
    const [loadingSecciones, setLoadingSecciones] = useState({});
    const [downloadingAudio, setDownloadingAudio] = useState(null);

    // Audio state
    const [currentAudio, setCurrentAudio] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1.0);
    const audioRef = useRef(new Audio());
    const toast = useToast();

    useEffect(() => {
        loadTemas();

        // Cleanup audio on unmount
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
        };
    }, []);

    const loadTemas = async () => {
        try {
            const data = await ope.getTemas();
            setTemas(data);
        } catch (error) {
            console.error('Error loading temas:', error);
            toast.error('Error al cargar el temario');
        } finally {
            setLoading(false);
        }
    };

    const toggleTema = async (temaId) => {
        if (expandedTema === temaId) {
            setExpandedTema(null);
            return;
        }

        setExpandedTema(temaId);

        // Load secciones if not loaded yet
        if (!secciones[temaId]) {
            setLoadingSecciones(prev => ({ ...prev, [temaId]: true }));
            try {
                const data = await ope.getSecciones(temaId);
                setSecciones(prev => ({ ...prev, [temaId]: data }));
            } catch (error) {
                console.error('Error loading secciones:', error);
                toast.error('Error al cargar secciones');
            } finally {
                setLoadingSecciones(prev => ({ ...prev, [temaId]: false }));
            }
        }
    };

    const playAudio = (filename, titulo) => {
        // If same audio, toggle play/pause
        if (currentAudio?.filename === filename) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
            return;
        }

        // New audio
        // New audio
        // api.js doesn't expose getUri publicly in the default export, but we can assume /api
        // Actually, let's use the helper if available or fallback
        // Note: api.js did not export getUri in my last edit, let's assume relative path works for web
        // For native, we might need full URL. api.js handles generic requests but for audio src we need URL.

        // Construct URL manually based on environment (simplified for now)
        const audioUrl = `${API_BASE}/ope/audio/${filename}`;
        // For now, let's rely on relative path for web and handle native later.
        // Actually, userApi is imported but getUri might not be there.

        // Let's try to get a token for auth (since endpoints are protected)
        // Audio tag src with bearer token is tricky.
        // Solution: Fetch as blob and create object URL OR use a token in query param (less secure but works for media)
        // Or simpler: use XHR/Fetch to get blob.

        setDownloadingAudio(filename);

        const token = localStorage.getItem('token');

        fetch(audioUrl, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(async response => {
                if (!response.ok) throw new Error('Error de red o archivo no encontrado');
                return response.blob();
            })
            .then(blob => {
                const url = URL.createObjectURL(blob);
                audioRef.current.src = url;
                audioRef.current.playbackRate = playbackRate;
                audioRef.current.play();

                setCurrentAudio({ filename, titulo });
                setIsPlaying(true);

                // Cleanup old blob url when switching? 
                // React handles cleanup on unmount but switching tracks leaves old URLs.
                // Ideally we revoke old URL.
            })
            .catch(err => {
                console.error('Error playing audio:', err);
                toast.error('Error al reproducir audio');
            })
            .finally(() => setDownloadingAudio(null));

        if ('mediaSession' in navigator) {
            navigator.mediaSession.metadata = new MediaMetadata({
                title: titulo,
                artist: 'TUVISA OPE',
                album: 'Temario de Estudio',
                artwork: [
                    { src: '/pwa-192x192.png', sizes: '192x192', type: 'image/png' }
                ]
            });
            navigator.mediaSession.setActionHandler('play', () => {
                audioRef.current.play();
                setIsPlaying(true);
            });
            navigator.mediaSession.setActionHandler('pause', () => {
                audioRef.current.pause();
                setIsPlaying(false);
            });
        }
    };

    const handleScheduleReminder = async () => {
        const result = await NativeService.scheduleNotification({
            title: '⏰ Hora de repasar',
            body: 'Continúa con tu estudio de la OPE de TUVISA. ¡Ánimo!',
            schedule: { at: new Date(Date.now() + 1000 * 60 * 60 * 24) } // 24 hours
        });

        if (result.success) {
            toast.success(result.simulated ? 'Recordatorio simulado (Web)' : 'Recordatorio programado para mañana');
        } else {
            toast.error('No se pudo programar el recordatorio');
        }
    };

    // Handle playback rate change
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.playbackRate = playbackRate;
        }
    }, [playbackRate]);

    // Audio event listeners
    useEffect(() => {
        const audio = audioRef.current;

        const handleEnded = () => setIsPlaying(false);
        const handlePause = () => setIsPlaying(false);
        const handlePlay = () => setIsPlaying(true);

        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('play', handlePlay);

        return () => {
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('play', handlePlay);
        };
    }, []);

    const changeSpeed = () => {
        const speeds = [1.0, 1.25, 1.5, 2.0];
        const currentIndex = speeds.indexOf(playbackRate);
        const nextIndex = (currentIndex + 1) % speeds.length;
        setPlaybackRate(speeds[nextIndex]);
    };

    if (loading && temas.length === 0) {
        return <div className="p-8 text-center text-[var(--text-muted)]">Cargando temario...</div>;
    }

    return (
        <div className="space-y-4 pb-24">
            {/* Native Actions */}
            <div className="flex justify-end px-2">
                <button
                    onClick={handleScheduleReminder}
                    className="flex items-center gap-2 text-xs font-medium text-cyan-700 dark:text-[var(--accent-label)] hover:opacity-80 bg-cyan-100 dark:bg-[var(--glass-bg)] px-3 py-1.5 rounded-full border border-cyan-200 dark:border-[var(--glass-border)] transition-colors"
                >
                    <Bell className="w-3.5 h-3.5" />
                    Recordar estudiar mañana
                </button>
            </div>
            {/* Player Floating Controls (if active) */}
            {currentAudio && (
                <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-8 md:bottom-8 md:w-96 bg-[var(--sidebar-bg)] border border-[var(--glass-border)] rounded-xl shadow-2xl p-4 z-50 backdrop-blur-lg">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => playAudio(currentAudio.filename, currentAudio.titulo)}
                            className="w-12 h-12 rounded-full bg-cyan-500 text-white flex items-center justify-center shadow-lg hover:bg-cyan-400 transition-colors"
                            disabled={downloadingAudio === currentAudio.filename}
                        >
                            {downloadingAudio === currentAudio.filename ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : isPlaying ? (
                                <Pause className="w-5 h-5 fill-current" />
                            ) : (
                                <Play className="w-5 h-5 fill-current ml-1" />
                            )}
                        </button>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[var(--text-primary)] truncate">{currentAudio.titulo}</p>
                            <p className="text-xs text-cyan-400">Reproduciendo</p>
                        </div>
                        <button
                            onClick={changeSpeed}
                            className="px-2 py-1 rounded bg-[var(--glass-bg)] text-xs font-bold text-[var(--text-primary)] hover:bg-[var(--glass-border)] transition-colors"
                        >
                            {playbackRate}x
                        </button>
                    </div>
                </div>
            )}

            {/* Topics List */}
            {temas.filter(t => t.num_audios > 0).map(tema => (
                <div key={tema.id} className="bg-[var(--bg-secondary)] border border-[var(--glass-border)] rounded-xl overflow-hidden shadow-sm">
                    <button
                        onClick={() => toggleTema(tema.id)}
                        className="w-full p-4 flex items-center justify-between hover:bg-[var(--glass-bg)] transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center font-bold text-sm">
                                {tema.orden}
                            </div>
                            <div className="text-left">
                                <h3 className="text-[var(--text-primary)] font-medium">{tema.titulo}</h3>
                                <p className="text-xs text-[var(--text-muted)]">{tema.descripcion}</p>
                            </div>
                        </div>
                        {expandedTema === tema.id ? (
                            <ChevronUp className="w-5 h-5 text-[var(--text-muted)]" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-[var(--text-muted)]" />
                        )}
                    </button>

                    {/* Sections */}
                    {expandedTema === tema.id && (
                        <div className="border-t border-[var(--glass-border)] bg-[var(--bg-primary)]">
                            {loadingSecciones[tema.id] ? (
                                <div className="p-4 text-center text-xs text-[var(--text-muted)]">Cargando secciones...</div>
                            ) : secciones[tema.id]?.length === 0 ? (
                                <div className="p-4 text-center text-xs text-[var(--text-muted)]">No hay audios disponibles</div>
                            ) : (
                                <div className="divide-y divide-[var(--glass-border)]">
                                    {secciones[tema.id]?.map(seccion => (
                                        <div key={seccion.id} className="p-3 pl-14 flex items-center justify-between hover:bg-[var(--glass-bg)] transition-colors">
                                            <div className="flex-1 min-w-0 mr-4">
                                                <p className="text-sm text-[var(--text-primary)] truncate">{seccion.titulo}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Clock className="w-3 h-3 text-[var(--text-muted)]" />
                                                    <span className="text-xs text-[var(--text-muted)]">
                                                        {Math.floor(seccion.duracion_segundos / 60)}:{(seccion.duracion_segundos % 60).toString().padStart(2, '0')}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => playAudio(seccion.audio_filename, seccion.titulo)}
                                                    disabled={downloadingAudio === seccion.audio_filename}
                                                    className={`p-2 rounded-full transition-colors ${(currentAudio?.filename === seccion.audio_filename && isPlaying) || downloadingAudio === seccion.audio_filename
                                                        ? 'bg-cyan-500/20 text-cyan-400'
                                                        : 'bg-[var(--glass-bg)] text-[var(--text-muted)] hover:bg-[var(--glass-border)] hover:text-[var(--text-primary)]'
                                                        }`}
                                                >
                                                    {downloadingAudio === seccion.audio_filename ? (
                                                        <Loader2 className="w-4 h-4 animate-spin text-cyan-500" />
                                                    ) : currentAudio?.filename === seccion.audio_filename && isPlaying ? (
                                                        <Pause className="w-4 h-4 fill-current" />
                                                    ) : (
                                                        <Play className="w-4 h-4 fill-current ml-0.5" />
                                                    )}
                                                </button>
                                                {/* Download button - could link to same blob logic or direct link if feasible */}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default AudioTab;
